/**
 * Helper murni halaman Daftar Merek (kartu) & form merek `/admin/brands/[id]`
 * — tanpa I/O supaya bisa diuji unit tanpa mock Supabase (pola
 * `paymentAccounts.ts` / `expeditions.ts`).
 *
 * Dua urusan di sini:
 *   1. LINK TELEGRAM MEREK (`product_brands.telegram_url`, mig 3440) —
 *      normalisasi input bebas (`@nama`, `t.me/…`, link undangan `+kode`)
 *      menjadi satu bentuk kanonik `https://t.me/…`.
 *   2. GANTI NAMA MEREK — pra-cek tabrakan nama & ringkasan dampaknya, cermin
 *      klien dari guard di RPC `admin_rename_brand`. `product_brands.id`
 *      TIDAK PERNAH ikut berubah: dompet Saldo Merek (`brand_balance`) dan
 *      ledgernya menempel pada id itu (mig 154).
 */

import { normalizeIconUrl } from "@/lib/iconUrl";

// ─── Link Telegram merek ─────────────────────────────────────────────────────

/**
 * Username Telegram: 5–32 karakter, diawali huruf, sisanya huruf/angka/`_`.
 * Di sini ia hanya SALAH SATU bentuk yang diterima (lihat pola link undangan
 * di bawah).
 */
const USERNAME_RE = /^[A-Za-z][A-Za-z0-9_]{4,31}$/;
/** Link undangan grup/channel privat: `t.me/+AbC…` atau `t.me/joinchat/AbC…`. */
const INVITE_RE = /^\+[A-Za-z0-9_-]{6,}$/;
const JOINCHAT_RE = /^joinchat\/[A-Za-z0-9_-]{6,}$/;

/**
 * Normalisasi input link Telegram merek → bentuk kanonik `https://t.me/<path>`,
 * atau `null` bila bukan tautan Telegram yang bisa dikenali.
 *
 * Sengaja LONGGAR: merek boleh menautkan channel publik, grup, ATAU link
 * undangan privat — ketiganya sah sebagai "link Telegram merek".
 *
 * Diterima: `@aden` · `aden` · `t.me/aden` · `https://t.me/s/aden` ·
 * `telegram.me/aden` · `https://t.me/+AbCdEf12345` · `t.me/joinchat/AbCdEf123`.
 * Ditolak: host lain (`wa.me/…`, `instagram.com/…`), username terlalu pendek,
 * dan teks bebas.
 */
export function normalizeBrandTelegramUrl(raw: string | null | undefined): string | null {
  let s = (raw ?? "").trim();
  if (!s) return null;

  // Buang skema + host Telegram bila ada. Host SELAIN t.me/telegram.me ditolak
  // — kalau ada "://" atau titik di segmen pertama tapi bukan host Telegram,
  // ini tautan platform lain, bukan sekadar username.
  const hadScheme = /^https?:\/\//i.test(s);
  s = s.replace(/^https?:\/\//i, "");
  const isTelegramHost = /^(www\.)?(t|telegram)\.me\//i.test(s);
  if (isTelegramHost) {
    s = s.replace(/^(www\.)?(t|telegram)\.me\//i, "");
  } else if (hadScheme || s.includes("/") || s.includes(".")) {
    // Ada skema/host/path tapi bukan Telegram → bukan link yang kita kenali.
    return null;
  }

  // Buang query/fragment; path preview `s/<user>` disetarakan dengan username.
  s = s.split(/[?#]/)[0] ?? "";
  s = s.replace(/\/+$/, "");
  s = s.replace(/^@/, "");
  const preview = /^s\/(.+)$/i.exec(s);
  if (preview) s = preview[1];

  if (!s) return null;
  if (INVITE_RE.test(s) || JOINCHAT_RE.test(s) || USERNAME_RE.test(s)) {
    return `https://t.me/${s}`;
  }
  return null;
}

/**
 * Pesan error (Indonesia) bila input link Telegram tak dikenali, atau `null`
 * bila lolos. KOSONG dianggap SAH — artinya "merek ini belum/tidak punya link"
 * (dan menyimpan kosong = menghapus link yang ada).
 */
export function validateBrandTelegramInput(raw: string | null | undefined): string | null {
  const s = (raw ?? "").trim();
  if (!s) return null;
  if (normalizeBrandTelegramUrl(s) === null) {
    return "Link Telegram tidak dikenali. Contoh: @namachannel, t.me/namachannel, atau link undangan https://t.me/+kode.";
  }
  return null;
}

// ─── Logo merek ──────────────────────────────────────────────────────────────

/**
 * Normalisasi URL logo merek (`product_brands.icon_url`, mig 096) → dipakai apa
 * adanya bila bentuknya URL `http(s)://…` atau path lokal berawalan `/`;
 * selain itu `null`.
 *
 * Gerbangnya kini DI-SHARE dengan ikon kategori (`normalizeIconUrl`) — kedua
 * kolom sama-sama teks bebas dengan mode gagal yang sama. Nama ber-"brand"
 * dipertahankan untuk pemanggil di jalur merek (badge halaman detail produk).
 */
export function normalizeBrandIconUrl(raw: string | null | undefined): string | null {
  return normalizeIconUrl(raw);
}

/**
 * Teks pendek untuk ditampilkan di kartu/tombol (`t.me/aden`). Kembalikan
 * string kosong bila belum ada link.
 */
export function brandTelegramLabel(url: string | null | undefined): string {
  const normalized = normalizeBrandTelegramUrl(url);
  if (!normalized) return "";
  return normalized.replace(/^https:\/\//, "");
}

// ─── Ganti nama merek ────────────────────────────────────────────────────────

export type BrandNameRef = { id: string; name: string };

/**
 * Apakah `name` sudah dipakai merek LAIN (selain `exceptId`)? Cermin klien
 * dari pra-cek di RPC `admin_rename_brand`, dipakai untuk mematikan tombol
 * Simpan sebelum request dikirim.
 *
 * CATATAN: daftar yang dipegang UI hanya berisi merek AKTIF, sedangkan unique
 * `(store_id, name)` (mig 3200) juga dipesan merek NONAKTIF. Jadi cek ini
 * MELENGKAPI, bukan menggantikan, guard server — bentrok dengan merek arsip
 * tetap baru ketahuan dari pesan RPC.
 */
export function brandNameTaken(
  brands: BrandNameRef[],
  name: string,
  exceptId?: string
): boolean {
  const key = (name ?? "").trim().toLowerCase();
  if (!key) return false;
  return brands.some(
    (b) => b.id !== exceptId && b.name.trim().toLowerCase() === key
  );
}

export type BrandFormDraft = {
  name: string;
  telegramUrl: string;
};

/**
 * Validasi draft form merek. Kembalikan pesan error pertama (Indonesia) atau
 * `null` bila lolos.
 */
export function validateBrandForm(
  draft: BrandFormDraft,
  brands: BrandNameRef[],
  brandId: string
): string | null {
  if (!draft.name.trim()) return "Nama merek wajib diisi.";
  if (brandNameTaken(brands, draft.name, brandId)) {
    return `Nama merek "${draft.name.trim()}" sudah dipakai merek lain.`;
  }
  return validateBrandTelegramInput(draft.telegramUrl);
}

/** Apakah draft berbeda dari nilai tersimpan (menyalakan tombol Simpan)? */
export function brandFormDirty(
  draft: BrandFormDraft,
  saved: { name: string; telegramUrl?: string | null }
): boolean {
  const nameChanged = draft.name.trim() !== (saved.name ?? "").trim();
  const telegramChanged =
    normalizeBrandTelegramUrl(draft.telegramUrl) !==
    normalizeBrandTelegramUrl(saved.telegramUrl);
  return nameChanged || telegramChanged;
}

/**
 * Apakah nama BERUBAH (bukan sekadar berbeda spasi tepi)? Dipakai UI untuk
 * memutuskan perlu-tidaknya memanggil RPC rename — dan untuk memunculkan
 * peringatan dampaknya ke produk.
 */
export function brandNameChanged(nextName: string, savedName: string): boolean {
  return (nextName ?? "").trim() !== (savedName ?? "").trim();
}

/**
 * Kalimat peringatan sebelum ganti nama: berapa produk yang snapshot mereknya
 * ikut diperbarui. Ditulis eksplisit karena inilah yang menjaga Saldo Merek &
 * Retur Produksi tetap mengenali produk merek ini setelah namanya berubah.
 */
export function brandRenameWarning(totalProducts: number): string {
  if (totalProducts <= 0) {
    return "Belum ada produk yang memakai merek ini, jadi ganti nama aman. Saldo Merek tetap menempel pada merek yang sama.";
  }
  return `${totalProducts} produk memakai merek ini — nama mereknya akan ikut diperbarui otomatis supaya Saldo Merek & Retur Produksi tetap mengenalinya.`;
}

/** Ringkasan hasil RPC `admin_rename_brand` untuk pesan sukses di UI. */
export type BrandRenameResult = {
  renamed: boolean;
  name: string;
  productsUpdated: number;
  targetsUpdated: number;
};

/** Koersi defensif atas jsonb balikan `admin_rename_brand`. */
export function mapBrandRenameResult(raw: any): BrandRenameResult {
  return {
    renamed: raw?.renamed === true,
    name: typeof raw?.name === "string" ? raw.name : "",
    productsUpdated: Number(raw?.products_updated ?? 0) || 0,
    targetsUpdated: Number(raw?.targets_updated ?? 0) || 0,
  };
}

/** Pesan sukses simpan, menyebut dampak yang benar-benar terjadi. */
export function brandSaveMessage(rename: BrandRenameResult | null): string {
  if (!rename?.renamed) return "Perubahan merek tersimpan.";
  const parts = [`Nama merek diubah menjadi "${rename.name}".`];
  if (rename.productsUpdated > 0) {
    parts.push(`${rename.productsUpdated} produk ikut diperbarui.`);
  }
  if (rename.targetsUpdated > 0) {
    parts.push(`${rename.targetsUpdated} target program poin ikut diperbarui.`);
  }
  return parts.join(" ");
}
